import styled from "styled-components";
import { useState, useContext, useEffect } from "react";
import { HotTopicsContext } from "../../Contexts/HotTopicsContext";
import Autocomplete from "@mui/material/Autocomplete";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";

import { Tooltip } from "primereact/tooltip";

const SectionWrapper = styled.div`
  padding-top: 20px;
  width: 100%;
  max-width: 700px;
  h2 {
    color: var(--accent-color-dark);
    font-weight: 600;
    font-size: 18px;
    margin-bottom: 10px;
  }
  p {
    font-size: 12px;
  }
`;
const SpacerLine = styled.div`
  width: 100%;
  border-bottom: 2px solid
    ${(props) => props.color || "var(--accent-color-dark)"};
  margin: 10px 0;
`;
const QuestionWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 700px;
  width: 100%;

  .p-multiselect {
    width: 100%;
  }

  .MuiAutocomplete-root {
    width: 100%;
  }
  .MuiAutocomplete-root input {
    text-transform: capitalize;
  }

  .MuiChip-label {
    text-transform: capitalize;
  }
`;

export default function HotTopicsMenu(props: Props) {
  const hotTopicOptions = useContext(HotTopicsContext);
  const { hotTopics, setHotTopics } = props;

  const publicHotTopics = hotTopicOptions.filter(
    (hotTopic) => hotTopic.status === "classified"
  );

  const escapeRegExp = (string: string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); // $& means the whole matched string
  };

  const customFilterOptions = (
    options: (HotTopic | string)[],
    { inputValue }: { inputValue: string }
  ): (HotTopic | string)[] => {
    const matcher = new RegExp(escapeRegExp(inputValue), "i");

    return options.filter((option) => {
      const label = typeof option === "string" ? option : option.label;
      return (
        matcher.test(label) ||
        (typeof option !== "string" &&
          option.keyWords?.some((keyword) => matcher.test(keyword)))
      );
    });
  };

  return (
    <SectionWrapper>
      <h2>
        Hot Topics <Tooltip target=".hotTopTooltip" position="bottom" />
        <i
          className="hotTopTooltip pi pi-info-circle"
          data-pr-tooltip="Please share three significant topics or issues—be it personal, societal, political, cultural, church-related, or media influences—that are affecting our church community. Feel free to select from the propositions or suggest other subjects. Please note: Your suggestions are anonymized and will not be tracked back to you."
        ></i>
      </h2>
      <SpacerLine />
      <QuestionWrapper>
        <Autocomplete
          multiple
          id="tags-filled"
          options={publicHotTopics}
          filterOptions={customFilterOptions}
          freeSolo
          placeholder="Be as specific as possible"
          getOptionLabel={(option: HotTopic | string) =>
            typeof option === "string" ? option : option.label
          }
          renderTags={(values: readonly (HotTopic | string)[], getTagProps) =>
            values.map((value, index) => {
              const label = typeof value === "string" ? value : value.label;
              return (
                <Chip
                  variant="outlined"
                  label={label}
                  {...getTagProps({ index })}
                />
              );
            })
          }
          renderInput={(params) => <TextField {...params} />}
          onChange={(e, value) => {
            if (value.length <= 3) {
              const newHotTopicsLabels = value.map((v) =>
                typeof v === "string" ? v : v.label
              );
              setHotTopics(newHotTopicsLabels);
            }
          }}
          onBlur={(e) => {
            const target = e.target as HTMLInputElement; // Type assertion here
            const inputValue = target.value;
            if (inputValue && !hotTopics.includes(inputValue)) {
              setHotTopics([...hotTopics, inputValue]);
            }
          }}
          value={hotTopics}
          handleHomeEndKeys
          selectOnFocus
          clearOnBlur
        />
      </QuestionWrapper>
    </SectionWrapper>
  );
}

interface Props {
  hotTopics: string[];
  setHotTopics: (hotTopics: string[]) => void;
}
